import type { ReactNode } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import type { UserRole } from '@padhai/shared';
import { useAuth } from '../lib/auth.js';
import { Button, Card, Skeleton } from './ui.js';

/**
 * Route guards. These shape what the app shows; they are not the security
 * boundary — every route the guard protects is also enforced on the server.
 */

/**
 * What shows while the session or a page's code is on its way: the same
 * skeleton the pages use for their data, so a slow phone sees one kind of
 * waiting rather than a flash of text and then a shimmer.
 */
export function PageLoading() {
  return (
    <div className="space-y-4" role="status" aria-label="Loading">
      <Skeleton className="h-9 w-56" />
      <Skeleton className="h-4 w-72 max-w-full" />
      <Skeleton className="h-48" />
    </div>
  );
}

const Waiting = PageLoading;

/**
 * The session check failed for a reason other than "signed out": the server
 * errored or the phone has no connection. Sending a student with a valid
 * session to the sign-in page would sign them out in effect, so this says
 * what is true and offers the retry, which also runs by itself on `online`.
 */
export function CouldNotReach() {
  const { retry, retrying } = useAuth();
  return (
    <div className="mx-auto max-w-md py-10">
      <Card className="p-6 text-center">
        <h1 className="text-xl font-semibold">Could not reach Padhai</h1>
        <p className="mt-2 text-sm text-ink-soft">
          Padhai did not answer, so this page could not check your sign-in. Check your connection;
          it tries again by itself when you are back online.
        </p>
        <Button variant="primary" className="mt-5 w-full" loading={retrying} onClick={retry}>
          Try again
        </Button>
      </Card>
    </div>
  );
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading, unreachable } = useAuth();
  const location = useLocation();

  if (loading) return <Waiting />;
  if (!user && unreachable) return <CouldNotReach />;
  if (!user) {
    // Remember where they were headed so sign-in can finish the journey.
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }
  return <>{children}</>;
}

export function RequireRole({ roles, children }: { roles: UserRole[]; children: ReactNode }) {
  const { user, loading, unreachable } = useAuth();
  const location = useLocation();

  if (loading) return <Waiting />;
  if (!user && unreachable) return <CouldNotReach />;
  if (!user)
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;

  if (!roles.includes(user.role)) {
    const { title, body, home } = notForYou(user.role, roles);
    return (
      <section className="rounded-card bg-surface p-5 shadow-card">
        <h1 className="text-xl font-semibold">{title}</h1>
        <p className="mt-2 text-sm text-ink-soft">{body}</p>
        <Link
          to="/"
          className="mt-4 inline-flex min-h-11 items-center text-sm font-medium text-brand-strong hover:underline"
        >
          {home}
        </Link>
      </section>
    );
  }

  return <>{children}</>;
}

const HOME_NAME: Record<UserRole, string> = {
  student: 'Go to Today',
  teacher: 'Go to your class',
  admin: 'Go to Usage',
};

/**
 * Says who a page is for and where this person's own work is, rather than one
 * sentence for everybody. A teacher landing on a student's chapter link was
 * told the page was "for teachers and school staff".
 */
export function notForYou(
  role: UserRole,
  allowed: UserRole[],
): { title: string; body: string; home: string } {
  const home = HOME_NAME[role];
  if (allowed.length === 1 && allowed[0] === 'student') {
    return {
      title: 'This is a student page',
      body:
        role === 'admin'
          ? 'Chapters are opened and studied from a student account. To see how a student is doing, open them from People.'
          : 'Chapters are studied from a student account. To see how a student is doing, open them from your class.',
      home,
    };
  }
  if (allowed.length === 1 && allowed[0] === 'admin') {
    return {
      title: 'This page is for the school admin',
      body:
        role === 'student'
          ? 'It is not part of your account. Your chapters are on Today.'
          : 'Ask your school admin if you need something changed here.',
      home,
    };
  }
  // Teachers and admins.
  return {
    title: 'This page is for teachers',
    body: 'It is not part of a student account. Your own chapters are on Today.',
    home,
  };
}
